import { useState } from 'react';
import { useCurrentStateAndParams } from '@uirouter/react';
import { Pod } from 'kubernetes-types/core/v1';

import { getPodLogs } from '@/react/kubernetes/applications/axios/pods/getPodLogs';
import { GenericResource } from '@/react/kubernetes/helm/types';

import { LogViewer } from '@@/LogViewer';
import { GetLogsParamsInterface } from '@@/LogViewer/types';
import { PortainerSelect } from '@@/form-components/PortainerSelect';
import { Widget, WidgetBody } from '@@/Widget';
import { Alert } from '@@/Alert';

import { useAddonReleaseScope } from './queries/useAddonReleaseScope';
import { AddonReleaseGuard } from './AddonReleaseGuard';
import { useReleasePods } from './queries/useReleasePods';

// Kept fresh so the pod list reflects restarts/new workloads while viewing logs.
const RELEASE_REFRESH_INTERVAL = 10000;

export function AddonLogsTab() {
  const { params } = useCurrentStateAndParams();
  const id = String(params.id ?? '');
  const { scope, query, environmentId, namespace } = useAddonReleaseScope(id, {
    showResources: true,
    refetchInterval: RELEASE_REFRESH_INTERVAL,
  });

  return (
    <AddonReleaseGuard scope={scope}>
      <AddonLogsViewer
        environmentId={environmentId ?? 0}
        namespace={namespace}
        resources={query.data?.info?.resources ?? []}
        // Without a release, offer every pod in the namespace to debug a
        // failing install.
        namespaceWide={scope === 'namespaceOnly'}
      />
    </AddonReleaseGuard>
  );
}

function AddonLogsViewer({
  environmentId,
  namespace,
  resources,
  namespaceWide,
}: {
  environmentId: number;
  namespace: string;
  resources: GenericResource[];
  namespaceWide: boolean;
}) {
  const podsQuery = useReleasePods(environmentId, namespace, resources, {
    allPods: namespaceWide,
  });
  const pods = podsQuery.data ?? [];

  const [selectedPod, setSelectedPod] = useState<string>();
  const [selectedContainer, setSelectedContainer] = useState<string>();

  // Pods whose containers haven't started yet (ContainerCreating, image pull,
  // init) have no logs and error if fetched, so only offer ones we can read.
  const loadablePods = pods.filter(
    (pod) => startedContainerNames(pod).length > 0
  );
  const podNames = loadablePods
    .map((pod) => pod.metadata?.name ?? '')
    .filter(Boolean);
  // Fall back to the first pod when the selected one is gone (pod churn).
  const activePod =
    selectedPod && podNames.includes(selectedPod) ? selectedPod : podNames[0];

  const containers = startedContainerNames(
    loadablePods.find((pod) => pod.metadata?.name === activePod)
  );
  const activeContainer =
    selectedContainer && containers.includes(selectedContainer)
      ? selectedContainer
      : containers[0];

  if (podsQuery.isInitialLoading) {
    return (
      <div className="row">
        <div className="col-sm-12">
          <Widget>
            <WidgetBody loading />
          </Widget>
        </div>
      </div>
    );
  }

  if (!activePod) {
    return (
      <div className="row">
        <div className="col-sm-12">
          <Widget>
            <WidgetBody>
              <Alert color="info">
                {pods.length
                  ? "Pods haven't started yet - logs will be available once their containers start."
                  : 'No pods found for this add-on.'}
              </Alert>
            </WidgetBody>
          </Widget>
        </div>
      </div>
    );
  }

  return (
    // Remount on pod/container change: the logs query key doesn't include the
    // container, so a fresh mount is the clean way to refetch on either switch.
    <LogViewer
      key={`${activePod}::${activeContainer ?? ''}`}
      resourceType="kube-pod"
      resourceName={activePod}
      getLogsFn={getLogsFn}
      useTimeOptions
      headerRow={
        <>
          <div className="flex items-center gap-2">
            <span className="text-sm">Pod</span>
            <div className="w-64">
              <PortainerSelect
                size="sm"
                value={activePod}
                options={podNames.map((name) => ({
                  label: name,
                  value: name,
                }))}
                onChange={(value) => setSelectedPod(value ?? undefined)}
                isClearable={false}
                bindToBody
                aria-label="Select pod"
                data-cy="addon-logs-pod-select"
              />
            </div>
          </div>
          {containers.length > 1 && (
            <div className="flex items-center gap-2">
              <span className="text-sm">Container</span>
              <div className="w-64">
                <PortainerSelect
                  size="sm"
                  value={activeContainer}
                  options={containers.map((name) => ({
                    label: name,
                    value: name,
                  }))}
                  onChange={(value) => setSelectedContainer(value ?? undefined)}
                  isClearable={false}
                  bindToBody
                  aria-label="Select container"
                  data-cy="addon-logs-container-select"
                />
              </div>
            </div>
          )}
        </>
      }
    />
  );

  function getLogsFn({
    timestamps,
    tail,
    sinceSeconds,
  }: GetLogsParamsInterface) {
    return getPodLogs(environmentId, namespace, activePod ?? '', {
      container: activeContainer,
      timestamps,
      tailLines: tail || 0,
      sinceSeconds,
    });
  }
}

// Names of the pod's containers that have started (running or terminated); a
// container still waiting to start has no logs to fetch.
function startedContainerNames(pod?: Pod): string[] {
  return (pod?.status?.containerStatuses ?? [])
    .filter((status) => !status.state?.waiting)
    .map((status) => status.name);
}
